import React from "react";

import { getElementBounds, pickElementAtPoint } from "@lib/canvas/elementGeometry";
import { isElementInteractive } from "@lib/elementVisibility";
import {
  findElementHandleAtPoint,
  getElementFrameRotationDeg,
  resizeElementFromHandle,
} from "@lib/canvas/elementHandles";
import { findPatternHandleAtPoint } from "@lib/canvas/patternOverlay";
import { projectPanelPoint, screenPointToPanel, type CanvasTransform } from "@lib/canvas/transform";
import { type ClearanceLines } from "@lib/clearance";
import { snapPointToGrid } from "@lib/grid";
import {
  PanelElementType,
  type MountingHole,
  type PanelElement,
  type PanelModel,
  type PanelOptions,
  type SvgArtworkElementProperties,
  type Vector2,
} from "@lib/panelTypes";
import type { PatternGuides, PatternHandleId } from "@lib/patternTool";
import {
  getReferenceImageControlPositions,
  getReferenceImageHandleDirection,
  getReferenceImageRotationRad,
  isPointInReferenceImage,
  resizeReferenceImageFromHandle,
  REFERENCE_IMAGE_HANDLE_HIT_RADIUS_PX,
  REFERENCE_IMAGE_RESIZE_HANDLES,
  REFERENCE_IMAGE_ROTATION_HANDLE_OFFSET_PX,
  type ReferenceImage,
  type ReferenceImageControlHandle,
  type ReferenceImageResizeHandle,
} from "@lib/referenceImage";
import { isSvgArtworkElement } from "@lib/svgArtwork";

import * as styles from "./PanelCanvas.css";

const MOUNTING_HOLE_HIT_PADDING_MM = 1;
const CLEARANCE_LINE_HIT_MM = 3;
const REFERENCE_IMAGE_HIT_PADDING_MM = 3;
const DEFAULT_CANVAS_CURSOR = "crosshair";
const REFERENCE_IMAGE_CONTROL_HANDLES = [...REFERENCE_IMAGE_RESIZE_HANDLES, "rotate"] as const;

type DraftPropertiesState = Partial<{
  [T in PanelElementType]: PanelElement["properties"];
}>;

type MoveState = {
  anchorId: string;
  pointerOffset: Vector2;
  startPositions: Record<string, Vector2>;
  elementIds: string[];
};

type ReferenceInteractionState =
  | {
      type: "move";
      offset: Vector2;
    }
  | {
      type: "resize";
      handle: ReferenceImageResizeHandle;
      startImage: ReferenceImage;
    }
  | {
      type: "rotate";
      startImage: ReferenceImage;
      pointerAngleOffsetRad: number;
    };

type SvgArtworkControlBox = Pick<
  ReferenceImage,
  "positionMm" | "rotationDeg" | "widthMm" | "heightMm"
>;

type SvgArtworkInteractionState =
  | {
      type: "resize";
      elementId: string;
      handle: ReferenceImageResizeHandle;
      startBox: SvgArtworkControlBox;
      historyPushed: boolean;
    }
  | {
      type: "rotate";
      elementId: string;
      startBox: SvgArtworkControlBox;
      pointerAngleOffsetRad: number;
      historyPushed: boolean;
    };

type ElementResizeInteractionState = {
  elementId: string;
  handle: ReferenceImageResizeHandle;
  startElement: PanelElement;
  startPointerMm: Vector2;
  historyPushed: boolean;
};

type PointerMode =
  | "idle"
  | "pan"
  | "click"
  | "move"
  | "marquee"
  | "clearance"
  | "reference-move"
  | "reference-resize"
  | "reference-rotate"
  | "svg-artwork-resize"
  | "svg-artwork-rotate"
  | "element-resize"
  | "pattern-handle";

/** A running mirror or pattern command, as the pointer drives it. */
export interface CanvasPatternPointer {
  guides: PatternGuides;
  /** Clicks place the command's reference instead of selecting. */
  awaitingReference: boolean;
  onPick: (pointMm: Vector2, snap: boolean) => void;
  onDragHandle: (handle: PatternHandleId, pointMm: Vector2, snap: boolean) => void;
}

/** A right click that did not drag, to open the context menu there. */
export interface CanvasContextMenuRequest {
  clientX: number;
  clientY: number;
  /** The element under the pointer, if any. */
  elementId: string | null;
}

interface SelectionOverlay {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface CanvasPointerResult {
  selectionOverlay: SelectionOverlay | null;
  pointerPanelPos: Vector2 | null;
  referenceImageElement: HTMLImageElement | null;
  snapOverridden: boolean;
  canvasClassName: string;
  canvasCursor: React.CSSProperties["cursor"];
  handlePointerDown: (event: React.PointerEvent<HTMLCanvasElement>) => void;
  handlePointerMove: (event: React.PointerEvent<HTMLCanvasElement>) => void;
  handlePointerUp: (event: React.PointerEvent<HTMLCanvasElement>) => void;
  handlePointerLeave: (event: React.PointerEvent<HTMLCanvasElement>) => void;
  handleContextMenu: (event: React.MouseEvent<HTMLCanvasElement>) => void;
  /** The pattern handle under the pointer or being dragged. */
  activePatternHandle: PatternHandleId | null;
}

interface CanvasPointerOptions {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  transform: CanvasTransform;
  model: PanelModel;
  mountingHoles: MountingHole[];
  referenceImage: ReferenceImage | null;
  referenceImageSelected: boolean;
  mountingHolesSelected: boolean;
  zoom: number;
  zoomLimits: { min: number; max: number };
  pan: Vector2;
  placementType: PanelElementType | null;
  draftProperties: DraftPropertiesState;
  displayOptions: Pick<
    PanelOptions,
    "showGrid" | "showMountingHoles" | "gridSizeMm" | "snapToGrid"
  >;
  selectedElementIds: string[];
  canvasSize: Vector2;
  onPlaceElement: (type: PanelElementType, positionMm: Vector2) => string;
  onMoveElement: (elementId: string, positionMm: Vector2) => void;
  onMoveElements: (updates: { id: string; positionMm: Vector2 }[]) => void;
  onMoveStart?: (elementId: string) => void;
  onMoveEnd?: () => void;
  onUpdateElement: (
    elementId: string,
    updater: (element: PanelElement) => PanelElement,
    options?: { skipHistory?: boolean },
  ) => void;
  onZoomChange: (zoom: number) => void;
  onPanChange: (pan: Vector2) => void;
  onSelectElement: (elementId: string | null) => void;
  onAddSelectedElements: (elementIds: string[]) => void;
  onSelectElements: (elementIds: string[]) => void;
  onToggleElementSelection: (elementId: string) => void;
  onClearSelection: () => void;
  onSelectReferenceImage: () => void;
  onClearReferenceSelection: () => void;
  onUpdateReferenceImage: (updates: Partial<ReferenceImage>) => void;
  /** Set while the reference image is being calibrated: clicks pick its points, nothing else. */
  referenceCalibrationPoints: Vector2[] | null;
  onPickCalibrationPoint: (pointMm: Vector2) => void;
  onSelectMountingHoles: () => void;
  onClearMountingHoleSelection: () => void;
  clearanceLines: ClearanceLines;
  onClearanceLineChange: (line: "top" | "bottom", positionMm: number) => void;
  onClearanceLineDragStart: () => void;
  onClearanceLineDragEnd: () => void;
  pattern: CanvasPatternPointer | null;
  onRequestContextMenu: (request: CanvasContextMenuRequest) => void;
}

// A right click that moves less than this opens the menu; more pans the view.
const CONTEXT_MENU_MAX_MOVE_PX = 4;

function cloneReferenceImage(image: ReferenceImage): ReferenceImage {
  return {
    ...image,
    positionMm: { ...image.positionMm },
  };
}

function getSvgArtworkControlBox(element: PanelElement): SvgArtworkControlBox | null {
  if (!isSvgArtworkElement(element)) {
    return null;
  }
  return {
    positionMm: { ...element.positionMm },
    rotationDeg: element.rotationDeg ?? 0,
    widthMm: element.properties.widthMm,
    heightMm: element.properties.heightMm,
  };
}

function isPointInMountingHole(point: Vector2, hole: MountingHole, paddingMm = 0): boolean {
  if (hole.shape === "slot" && hole.slotLengthMm) {
    const radius = hole.diameterMm / 2 + paddingMm;
    const halfLength = Math.max(hole.slotLengthMm / 2 + paddingMm, radius);
    const innerHalf = Math.max(halfLength - radius, 0);
    const dx = Math.abs(point.x - hole.center.x);
    const dy = Math.abs(point.y - hole.center.y);

    if (dx <= innerHalf) {
      return dy <= radius;
    }

    const distX = dx - innerHalf;
    return distX * distX + dy * dy <= radius * radius;
  }

  const radius = hole.diameterMm / 2 + paddingMm;
  const dx = point.x - hole.center.x;
  const dy = point.y - hole.center.y;
  return dx * dx + dy * dy <= radius * radius;
}

function findMountingHoleAtPoint(point: Vector2, holes: MountingHole[]): MountingHole | null {
  for (const hole of holes) {
    if (isPointInMountingHole(point, hole, MOUNTING_HOLE_HIT_PADDING_MM)) {
      return hole;
    }
  }
  return null;
}

function findClearanceLineAtPoint(point: Vector2, lines: ClearanceLines): "top" | "bottom" | null {
  const topDist = Math.abs(point.y - lines.topY);
  const bottomDist = Math.abs(point.y - lines.bottomY);
  const nearest = Math.min(topDist, bottomDist);
  if (nearest > CLEARANCE_LINE_HIT_MM) {
    return null;
  }
  return topDist <= bottomDist ? "top" : "bottom";
}

function getReferencePointerAngle(point: Vector2, center: Vector2): number {
  return Math.atan2(point.y - center.y, point.x - center.x);
}

function normalizeHalfTurn(angleRad: number): number {
  const halfTurn = Math.PI;
  let normalized = angleRad % halfTurn;
  if (normalized < 0) {
    normalized += halfTurn;
  }
  return normalized;
}

function getReferenceResizeCursor(
  handle: ReferenceImageResizeHandle,
  rotationDeg: number,
): React.CSSProperties["cursor"] {
  const direction = getReferenceImageHandleDirection(handle);
  const angle = Math.atan2(direction.y, direction.x) + (rotationDeg * Math.PI) / 180;
  const step = Math.round(normalizeHalfTurn(angle) / (Math.PI / 4)) % 4;

  switch (step) {
    case 0:
      return "ew-resize";
    case 1:
      return "nwse-resize";
    case 2:
      return "ns-resize";
    default:
      return "nesw-resize";
  }
}

export function useCanvasPointer({
  canvasRef,
  transform,
  model,
  mountingHoles,
  referenceImage,
  referenceImageSelected,
  mountingHolesSelected: _mountingHolesSelected,
  zoom,
  zoomLimits,
  pan,
  canvasSize,
  placementType,
  draftProperties: _draftProperties,
  displayOptions,
  selectedElementIds,
  onPlaceElement,
  onMoveElement,
  onMoveElements,
  onMoveStart,
  onMoveEnd,
  onUpdateElement,
  onZoomChange,
  onPanChange,
  onSelectElement,
  onAddSelectedElements,
  onSelectElements,
  onToggleElementSelection,
  onClearSelection,
  onSelectReferenceImage,
  onClearReferenceSelection,
  onUpdateReferenceImage,
  referenceCalibrationPoints,
  onPickCalibrationPoint,
  onSelectMountingHoles,
  onClearMountingHoleSelection,
  clearanceLines,
  onClearanceLineChange,
  onClearanceLineDragStart,
  onClearanceLineDragEnd,
  pattern,
  onRequestContextMenu,
}: CanvasPointerOptions): CanvasPointerResult {
  const panRef = React.useRef<Vector2>(pan);
  const pointerStartRef = React.useRef<Vector2 | null>(null);
  const pointerModeRef = React.useRef<PointerMode>("idle");
  const referenceInteractionRef = React.useRef<ReferenceInteractionState | null>(null);
  const svgArtworkInteractionRef = React.useRef<SvgArtworkInteractionState | null>(null);
  const elementResizeRef = React.useRef<ElementResizeInteractionState | null>(null);
  const panStartRef = React.useRef<Vector2 | null>(null);
  const moveStateRef = React.useRef<MoveState | null>(null);
  const patternHandleRef = React.useRef<PatternHandleId | null>(null);
  const panButtonRef = React.useRef<number | null>(null);
  const [activePatternHandle, setActivePatternHandle] = React.useState<PatternHandleId | null>(
    null,
  );
  const [isPanning, setIsPanning] = React.useState(false);
  const [pointerPanelPos, setPointerPanelPos] = React.useState<Vector2 | null>(null);
  const [snapOverridden, setSnapOverridden] = React.useState(false);
  const [selectionRect, setSelectionRect] = React.useState<{ start: Vector2; end: Vector2 } | null>(
    null,
  );
  const [isHoveringInteractive, setIsHoveringInteractive] = React.useState(false);
  const [canvasCursor, setCanvasCursor] =
    React.useState<React.CSSProperties["cursor"]>(DEFAULT_CANVAS_CURSOR);
  const selectionModeRef = React.useRef<"replace" | "add">("replace");
  const selectedElementSet = React.useMemo(() => new Set(selectedElementIds), [selectedElementIds]);
  const elementMap = React.useMemo(
    () => new Map(model.elements.map((element) => [element.id, element])),
    [model.elements],
  );
  // Hidden and locked elements cannot be picked, moved or resized on the canvas.
  const interactiveElements = React.useMemo(
    () => model.elements.filter(isElementInteractive),
    [model.elements],
  );
  const singleSelectedElement = React.useMemo(() => {
    const element =
      selectedElementIds.length === 1 ? (elementMap.get(selectedElementIds[0]) ?? null) : null;
    return element && isElementInteractive(element) ? element : null;
  }, [elementMap, selectedElementIds]);
  const [referenceImageElement, setReferenceImageElement] = React.useState<HTMLImageElement | null>(
    null,
  );

  React.useEffect(() => {
    if (!referenceImage) {
      setReferenceImageElement(null);
      return;
    }
    const img = new Image();
    img.onload = () => {
      setReferenceImageElement(img);
    };
    img.src = referenceImage.dataUrl;
  }, [referenceImage]);

  React.useEffect(() => {
    panRef.current = pan;
  }, [pan]);

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Shift") {
        setSnapOverridden(true);
      }
    };
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key === "Shift") {
        setSnapOverridden(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, []);

  const selectionOverlay = React.useMemo(() => {
    if (!selectionRect) {
      return null;
    }
    const startPx = projectPanelPoint(selectionRect.start, transform);
    const endPx = projectPanelPoint(selectionRect.end, transform);
    return {
      left: Math.min(startPx.x, endPx.x),
      top: Math.min(startPx.y, endPx.y),
      width: Math.abs(endPx.x - startPx.x),
      height: Math.abs(endPx.y - startPx.y),
    };
  }, [selectionRect, transform]);

  const clampZoom = React.useCallback(
    (value: number) => Math.min(zoomLimits.max, Math.max(zoomLimits.min, value)),
    [zoomLimits.max, zoomLimits.min],
  );

  const maybeSnap = React.useCallback(
    (point: Vector2): Vector2 =>
      displayOptions.snapToGrid && !snapOverridden
        ? snapPointToGrid(point, displayOptions.gridSizeMm, {
            x: model.dimensions.widthMm,
            y: model.dimensions.heightMm,
          })
        : point,
    [
      displayOptions.gridSizeMm,
      displayOptions.snapToGrid,
      snapOverridden,
      model.dimensions.heightMm,
      model.dimensions.widthMm,
    ],
  );

  const findReferenceControlAtPoint = React.useCallback(
    (pointPx: Vector2): ReferenceImageControlHandle | null => {
      if (!referenceImage || !referenceImageSelected || transform.scale <= 0) {
        return null;
      }

      const rotationOffsetMm =
        REFERENCE_IMAGE_ROTATION_HANDLE_OFFSET_PX / Math.max(transform.scale, 1);
      const controls = getReferenceImageControlPositions(referenceImage, rotationOffsetMm);
      const maxDistanceSq = REFERENCE_IMAGE_HANDLE_HIT_RADIUS_PX ** 2;
      let closestHandle: ReferenceImageControlHandle | null = null;
      let closestDistanceSq = Infinity;

      REFERENCE_IMAGE_CONTROL_HANDLES.forEach((handle) => {
        const controlPointPx = projectPanelPoint(controls[handle], transform);
        const dx = pointPx.x - controlPointPx.x;
        const dy = pointPx.y - controlPointPx.y;
        const distanceSq = dx * dx + dy * dy;
        if (distanceSq <= maxDistanceSq && distanceSq < closestDistanceSq) {
          closestHandle = handle;
          closestDistanceSq = distanceSq;
        }
      });

      return closestHandle;
    },
    [referenceImage, referenceImageSelected, transform],
  );

  const findSelectedElementControlAtPoint = React.useCallback(
    (pointPx: Vector2): ReferenceImageControlHandle | null =>
      singleSelectedElement
        ? findElementHandleAtPoint(singleSelectedElement, transform, pointPx)
        : null,
    [singleSelectedElement, transform],
  );

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const handleWheel = (event: WheelEvent | React.WheelEvent<HTMLCanvasElement>) => {
      if (event.cancelable) {
        event.preventDefault();
      }
      (event as WheelEvent).stopImmediatePropagation?.();
      (event as unknown as { stopPropagation?: () => void }).stopPropagation?.();

      const rect = canvas.getBoundingClientRect();
      const pointerPx = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
      const pointerPanel = screenPointToPanel(pointerPx, transform);
      if (!pointerPanel) {
        return;
      }

      const zoomFactor = event.deltaY > 0 ? 0.95 : 1.05;
      const nextZoom = clampZoom(zoom * zoomFactor);
      if (nextZoom === zoom || zoom === 0) {
        return;
      }

      const baseScale = transform.scale / zoom;
      const nextScale = baseScale * nextZoom;
      const nextPanelWidthPx = model.dimensions.widthMm * nextScale;
      const nextPanelHeightPx = model.dimensions.heightMm * nextScale;
      const nextPan = {
        x: pointerPx.x - pointerPanel.x * nextScale - (canvasSize.x - nextPanelWidthPx) / 2,
        y: pointerPx.y - pointerPanel.y * nextScale - (canvasSize.y - nextPanelHeightPx) / 2,
      };

      onZoomChange(nextZoom);
      onPanChange(nextPan);
    };

    const listener = (event: WheelEvent) => handleWheel(event);
    canvas.addEventListener("wheel", listener, { passive: false });
    return () => {
      canvas.removeEventListener("wheel", listener);
    };
  }, [
    canvasRef,
    clampZoom,
    model.dimensions.heightMm,
    model.dimensions.widthMm,
    onPanChange,
    onZoomChange,
    transform,
    zoom,
    canvasSize.x,
    canvasSize.y,
  ]);

  const updateHoverState = React.useCallback(
    (pointPanel: Vector2 | null, pointPx: Vector2 | null) => {
      if (!pointPanel || !pointPx) {
        setIsHoveringInteractive(false);
        setCanvasCursor(DEFAULT_CANVAS_CURSOR);
        return;
      }

      const pointerMode = pointerModeRef.current;
      if (pointerMode !== "idle" && pointerMode !== "click") {
        return;
      }

      if (referenceCalibrationPoints) {
        setIsHoveringInteractive(false);
        setCanvasCursor(DEFAULT_CANVAS_CURSOR);
        return;
      }

      if (pattern) {
        const handle = findPatternHandleAtPoint(pattern.guides, pointPx, transform);
        setActivePatternHandle(handle);
        if (handle) {
          setIsHoveringInteractive(true);
          setCanvasCursor("grab");
          return;
        }
        if (pattern.awaitingReference) {
          setIsHoveringInteractive(false);
          setCanvasCursor(DEFAULT_CANVAS_CURSOR);
          return;
        }
      }

      const selectedReferenceHandle = findReferenceControlAtPoint(pointPx);
      if (selectedReferenceHandle && referenceImage) {
        setIsHoveringInteractive(true);
        setCanvasCursor(
          selectedReferenceHandle === "rotate"
            ? "crosshair"
            : getReferenceResizeCursor(selectedReferenceHandle, referenceImage.rotationDeg),
        );
        return;
      }

      const selectedElementHandle = findSelectedElementControlAtPoint(pointPx);
      if (selectedElementHandle && singleSelectedElement) {
        setIsHoveringInteractive(true);
        setCanvasCursor(
          selectedElementHandle === "rotate"
            ? "crosshair"
            : getReferenceResizeCursor(
                selectedElementHandle,
                getElementFrameRotationDeg(singleSelectedElement),
              ),
        );
        return;
      }

      if (
        referenceImage &&
        referenceImageSelected &&
        isPointInReferenceImage(pointPanel, referenceImage, REFERENCE_IMAGE_HIT_PADDING_MM)
      ) {
        setIsHoveringInteractive(true);
        setCanvasCursor("grab");
        return;
      }

      const hole =
        displayOptions.showMountingHoles && findMountingHoleAtPoint(pointPanel, mountingHoles);
      const element = pickElementAtPoint(pointPanel, interactiveElements, {
        isOverMountingHole: Boolean(hole),
        isPlacing: placementType !== null,
        selectedIds: selectedElementSet,
      });
      if (element) {
        setIsHoveringInteractive(true);
        setCanvasCursor("pointer");
        return;
      }

      if (hole) {
        setIsHoveringInteractive(true);
        setCanvasCursor("pointer");
        return;
      }

      const clearanceHit = findClearanceLineAtPoint(pointPanel, clearanceLines);
      if (clearanceHit) {
        setIsHoveringInteractive(true);
        setCanvasCursor("ns-resize");
        return;
      }

      if (
        referenceImage &&
        isPointInReferenceImage(pointPanel, referenceImage, REFERENCE_IMAGE_HIT_PADDING_MM)
      ) {
        setIsHoveringInteractive(true);
        setCanvasCursor("grab");
        return;
      }

      setIsHoveringInteractive(false);
      setCanvasCursor(DEFAULT_CANVAS_CURSOR);
    },
    [
      clearanceLines,
      displayOptions.showMountingHoles,
      findReferenceControlAtPoint,
      findSelectedElementControlAtPoint,
      interactiveElements,
      mountingHoles,
      pattern,
      placementType,
      referenceCalibrationPoints,
      referenceImage,
      referenceImageSelected,
      selectedElementSet,
      singleSelectedElement,
      transform,
    ],
  );

  const isSnapActive = displayOptions.snapToGrid && !snapOverridden;

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    event.stopPropagation();

    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    canvas.setPointerCapture(event.pointerId);

    const additiveModifier = event.shiftKey || event.metaKey || event.ctrlKey;
    const shouldPan = event.button === 1 || event.button === 2 || event.altKey;
    pointerModeRef.current = shouldPan ? "pan" : "click";
    pointerStartRef.current = { x: event.clientX, y: event.clientY };
    panStartRef.current = shouldPan ? { ...panRef.current } : null;
    panButtonRef.current = shouldPan ? event.button : null;
    setIsHoveringInteractive(false);

    if (shouldPan) {
      event.preventDefault();
      setIsPanning(true);
      setCanvasCursor("grabbing");
      return;
    }

    const rect = canvas.getBoundingClientRect();
    const pointPx = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
    const pointPanel = screenPointToPanel(pointPx, transform);
    if (!pointPanel) {
      return;
    }

    // Points are picked where the image shows them, never snapped to the grid.
    if (referenceCalibrationPoints) {
      pointerModeRef.current = "idle";
      onPickCalibrationPoint(pointPanel);
      return;
    }

    if (pattern) {
      const handle = findPatternHandleAtPoint(pattern.guides, pointPx, transform);
      if (handle) {
        patternHandleRef.current = handle;
        setActivePatternHandle(handle);
        pointerModeRef.current = "pattern-handle";
        setCanvasCursor("grabbing");
        return;
      }
      if (pattern.awaitingReference) {
        pointerModeRef.current = "idle";
        pattern.onPick(pointPanel, isSnapActive);
        return;
      }
    }

    const selectedReferenceHandle = findReferenceControlAtPoint(pointPx);
    if (referenceImage && referenceImageSelected && selectedReferenceHandle) {
      onClearMountingHoleSelection();
      onSelectReferenceImage();

      if (selectedReferenceHandle === "rotate") {
        referenceInteractionRef.current = {
          type: "rotate",
          startImage: cloneReferenceImage(referenceImage),
          pointerAngleOffsetRad:
            getReferencePointerAngle(pointPanel, referenceImage.positionMm) -
            getReferenceImageRotationRad(referenceImage),
        };
        pointerModeRef.current = "reference-rotate";
        setCanvasCursor("crosshair");
        return;
      }

      referenceInteractionRef.current = {
        type: "resize",
        handle: selectedReferenceHandle,
        startImage: cloneReferenceImage(referenceImage),
      };
      pointerModeRef.current = "reference-resize";
      setCanvasCursor(
        getReferenceResizeCursor(selectedReferenceHandle, referenceImage.rotationDeg),
      );
      return;
    }

    if (
      referenceImage &&
      referenceImageSelected &&
      isPointInReferenceImage(pointPanel, referenceImage, REFERENCE_IMAGE_HIT_PADDING_MM)
    ) {
      pointerModeRef.current = "reference-move";
      referenceInteractionRef.current = {
        type: "move",
        offset: {
          x: referenceImage.positionMm.x - pointPanel.x,
          y: referenceImage.positionMm.y - pointPanel.y,
        },
      };
      onClearMountingHoleSelection();
      onSelectReferenceImage();
      setCanvasCursor("grabbing");
      return;
    }

    const selectedElementHandle = findSelectedElementControlAtPoint(pointPx);
    if (singleSelectedElement && selectedElementHandle) {
      // The reference image cannot be selected alongside an element, and clearing it would
      // also clear the element selection mid-drag.
      onClearMountingHoleSelection();

      const selectedSvgArtworkBox = getSvgArtworkControlBox(singleSelectedElement);
      if (selectedSvgArtworkBox) {
        if (selectedElementHandle === "rotate") {
          svgArtworkInteractionRef.current = {
            type: "rotate",
            elementId: singleSelectedElement.id,
            startBox: selectedSvgArtworkBox,
            pointerAngleOffsetRad:
              getReferencePointerAngle(pointPanel, selectedSvgArtworkBox.positionMm) -
              getReferenceImageRotationRad(selectedSvgArtworkBox),
            historyPushed: false,
          };
          pointerModeRef.current = "svg-artwork-rotate";
          setCanvasCursor("crosshair");
          return;
        }

        svgArtworkInteractionRef.current = {
          type: "resize",
          elementId: singleSelectedElement.id,
          handle: selectedElementHandle,
          startBox: selectedSvgArtworkBox,
          historyPushed: false,
        };
        pointerModeRef.current = "svg-artwork-resize";
        setCanvasCursor(
          getReferenceResizeCursor(selectedElementHandle, selectedSvgArtworkBox.rotationDeg),
        );
        return;
      }

      if (selectedElementHandle !== "rotate") {
        elementResizeRef.current = {
          elementId: singleSelectedElement.id,
          handle: selectedElementHandle,
          startElement: singleSelectedElement,
          startPointerMm: pointPanel,
          historyPushed: false,
        };
        pointerModeRef.current = "element-resize";
        setCanvasCursor(
          getReferenceResizeCursor(
            selectedElementHandle,
            getElementFrameRotationDeg(singleSelectedElement),
          ),
        );
        return;
      }
    }

    const hitHole = findMountingHoleAtPoint(pointPanel, mountingHoles);
    const element = pickElementAtPoint(pointPanel, interactiveElements, {
      isOverMountingHole: hitHole !== null,
      isPlacing: placementType !== null,
      selectedIds: selectedElementSet,
    });
    if (element) {
      onClearMountingHoleSelection();
      if (additiveModifier) {
        onToggleElementSelection(element.id);
        pointerModeRef.current = "idle";
        setCanvasCursor(DEFAULT_CANVAS_CURSOR);
        return;
      }

      const moveCandidates = selectedElementSet.has(element.id)
        ? selectedElementIds.filter((id) => {
            const selected = elementMap.get(id);
            return selected !== undefined && isElementInteractive(selected);
          })
        : [element.id];

      if (!selectedElementSet.has(element.id)) {
        onSelectElement(element.id);
      }

      const startPositions: Record<string, Vector2> = {};
      moveCandidates.forEach((id) => {
        const target = elementMap.get(id);
        if (target) {
          startPositions[id] = { ...target.positionMm };
        }
      });

      const elementIds = Object.keys(startPositions);
      moveStateRef.current = {
        anchorId: element.id,
        pointerOffset: {
          x: element.positionMm.x - pointPanel.x,
          y: element.positionMm.y - pointPanel.y,
        },
        startPositions,
        elementIds,
      };
      pointerModeRef.current = "move";
      setCanvasCursor("grabbing");
      onMoveStart?.(element.id);
      return;
    }

    if (hitHole) {
      pointerModeRef.current = "idle";
      onSelectMountingHoles();
      setCanvasCursor("pointer");
      return;
    }

    if (
      referenceImage &&
      isPointInReferenceImage(pointPanel, referenceImage, REFERENCE_IMAGE_HIT_PADDING_MM)
    ) {
      pointerModeRef.current = "reference-move";
      referenceInteractionRef.current = {
        type: "move",
        offset: {
          x: referenceImage.positionMm.x - pointPanel.x,
          y: referenceImage.positionMm.y - pointPanel.y,
        },
      };
      onClearMountingHoleSelection();
      onSelectReferenceImage();
      setCanvasCursor("grabbing");
      return;
    }

    const clearanceHit = findClearanceLineAtPoint(pointPanel, clearanceLines);
    if (clearanceHit) {
      pointerModeRef.current = "clearance";
      onClearanceLineDragStart();
      onClearanceLineChange(clearanceHit, pointPanel.y);
      setCanvasCursor("ns-resize");
      return;
    }

    if (placementType) {
      const snappedPoint = maybeSnap(pointPanel);
      onClearMountingHoleSelection();
      const newId = onPlaceElement(placementType, snappedPoint);
      onSelectElement(newId);
      pointerModeRef.current = "idle";
      setCanvasCursor(DEFAULT_CANVAS_CURSOR);
      return;
    }

    selectionModeRef.current = additiveModifier ? "add" : "replace";
    setSelectionRect({ start: pointPanel, end: pointPanel });
    onClearMountingHoleSelection();
    pointerModeRef.current = "marquee";
    setCanvasCursor(DEFAULT_CANVAS_CURSOR);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    event.stopPropagation();

    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    if (pointerModeRef.current === "pan") {
      if (!pointerStartRef.current || !panStartRef.current) {
        return;
      }

      const deltaX = event.clientX - pointerStartRef.current.x;
      const deltaY = event.clientY - pointerStartRef.current.y;
      const nextPan = {
        x: panStartRef.current.x + deltaX,
        y: panStartRef.current.y + deltaY,
      };

      onPanChange(nextPan);
      event.preventDefault();
      return;
    }

    const rect = canvas.getBoundingClientRect();
    const pointPx = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
    const pointPanel = screenPointToPanel(pointPx, transform);
    updateHoverState(pointPanel, pointPx);

    if (pointerModeRef.current === "pattern-handle") {
      if (pointPanel && pattern && patternHandleRef.current) {
        pattern.onDragHandle(patternHandleRef.current, pointPanel, isSnapActive);
      }
      return;
    }

    if (pointerModeRef.current === "clearance") {
      if (!pointPanel) {
        return;
      }
      const activeLine = findClearanceLineAtPoint(pointPanel, clearanceLines) ?? "top";
      onClearanceLineChange(activeLine, pointPanel.y);
      setPointerPanelPos(pointPanel);
      return;
    }

    if (pointerModeRef.current === "reference-move") {
      const interaction = referenceInteractionRef.current;
      if (!pointPanel || !referenceImage || !interaction || interaction.type !== "move") {
        return;
      }
      const snapped = maybeSnap({
        x: pointPanel.x + interaction.offset.x,
        y: pointPanel.y + interaction.offset.y,
      });
      onUpdateReferenceImage({ positionMm: snapped });
      setPointerPanelPos(pointPanel);
      return;
    }

    if (pointerModeRef.current === "reference-resize") {
      const interaction = referenceInteractionRef.current;
      if (!pointPanel || !interaction || interaction.type !== "resize") {
        return;
      }
      const nextPoint = maybeSnap(pointPanel);
      onUpdateReferenceImage(
        resizeReferenceImageFromHandle(interaction.startImage, interaction.handle, nextPoint),
      );
      setPointerPanelPos(pointPanel);
      return;
    }

    if (pointerModeRef.current === "reference-rotate") {
      const interaction = referenceInteractionRef.current;
      if (!pointPanel || !interaction || interaction.type !== "rotate") {
        return;
      }
      const nextRotationDeg =
        ((getReferencePointerAngle(pointPanel, interaction.startImage.positionMm) -
          interaction.pointerAngleOffsetRad) *
          180) /
        Math.PI;
      onUpdateReferenceImage({ rotationDeg: nextRotationDeg });
      setPointerPanelPos(pointPanel);
      return;
    }

    if (pointerModeRef.current === "svg-artwork-resize") {
      const interaction = svgArtworkInteractionRef.current;
      if (!pointPanel || !interaction || interaction.type !== "resize") {
        return;
      }
      const nextPoint = maybeSnap(pointPanel);
      const resized = resizeReferenceImageFromHandle(
        interaction.startBox,
        interaction.handle,
        nextPoint,
      );
      const skipHistory = interaction.historyPushed;
      interaction.historyPushed = true;
      onUpdateElement(
        interaction.elementId,
        (element) => {
          if (!isSvgArtworkElement(element)) {
            return element;
          }
          const properties: SvgArtworkElementProperties = {
            ...element.properties,
            widthMm: resized.widthMm,
            heightMm: resized.heightMm,
          };
          return {
            ...element,
            positionMm: resized.positionMm,
            properties,
          };
        },
        { skipHistory },
      );
      setPointerPanelPos(pointPanel);
      return;
    }

    if (pointerModeRef.current === "svg-artwork-rotate") {
      const interaction = svgArtworkInteractionRef.current;
      if (!pointPanel || !interaction || interaction.type !== "rotate") {
        return;
      }
      const nextRotationDeg =
        ((getReferencePointerAngle(pointPanel, interaction.startBox.positionMm) -
          interaction.pointerAngleOffsetRad) *
          180) /
        Math.PI;
      const skipHistory = interaction.historyPushed;
      interaction.historyPushed = true;
      onUpdateElement(
        interaction.elementId,
        (element) =>
          isSvgArtworkElement(element)
            ? {
                ...element,
                rotationDeg: nextRotationDeg,
              }
            : element,
        { skipHistory },
      );
      setPointerPanelPos(pointPanel);
      return;
    }

    if (pointerModeRef.current === "element-resize") {
      const interaction = elementResizeRef.current;
      if (!pointPanel || !interaction) {
        return;
      }
      const resized = resizeElementFromHandle(
        interaction.startElement,
        interaction.handle,
        {
          x: pointPanel.x - interaction.startPointerMm.x,
          y: pointPanel.y - interaction.startPointerMm.y,
        },
        { snap: displayOptions.snapToGrid && !snapOverridden },
      );
      const skipHistory = interaction.historyPushed;
      interaction.historyPushed = true;
      onUpdateElement(interaction.elementId, () => resized, { skipHistory });
      // Like a move, leave the pointer position alone so a placement ghost doesn't follow along.
      return;
    }

    if (pointerModeRef.current === "move") {
      if (!pointPanel || !moveStateRef.current) {
        return;
      }
      const { anchorId, pointerOffset, startPositions, elementIds } = moveStateRef.current;
      const anchorStart = startPositions[anchorId];
      if (!anchorStart) {
        return;
      }
      const targetAnchor = {
        x: pointPanel.x + pointerOffset.x,
        y: pointPanel.y + pointerOffset.y,
      };
      const snappedAnchor = maybeSnap(targetAnchor);
      const delta = {
        x: snappedAnchor.x - anchorStart.x,
        y: snappedAnchor.y - anchorStart.y,
      };
      const updates = elementIds.map((id) => {
        const start = startPositions[id];
        return {
          id,
          positionMm: {
            x: start.x + delta.x,
            y: start.y + delta.y,
          },
        };
      });
      if (!updates.length) {
        return;
      }
      if (updates.length === 1) {
        onMoveElement(updates[0].id, updates[0].positionMm);
      } else {
        onMoveElements(updates);
      }
      return;
    }

    if (pointerModeRef.current === "marquee") {
      if (!pointPanel) {
        return;
      }
      setSelectionRect((current) =>
        current
          ? {
              start: current.start,
              end: pointPanel,
            }
          : current,
      );
      setPointerPanelPos(pointPanel);
      return;
    }

    setPointerPanelPos(pointPanel);
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLCanvasElement>) => {
    event.stopPropagation();

    const canvas = canvasRef.current;

    if (pointerModeRef.current === "marquee" && selectionRect) {
      const bounds = {
        minX: Math.min(selectionRect.start.x, selectionRect.end.x),
        maxX: Math.max(selectionRect.start.x, selectionRect.end.x),
        minY: Math.min(selectionRect.start.y, selectionRect.end.y),
        maxY: Math.max(selectionRect.start.y, selectionRect.end.y),
      };
      const selectedIds = interactiveElements
        .filter((element) => {
          const elementBounds = getElementBounds(element);
          return !(
            elementBounds.maxX < bounds.minX ||
            elementBounds.minX > bounds.maxX ||
            elementBounds.maxY < bounds.minY ||
            elementBounds.minY > bounds.maxY
          );
        })
        .map((element) => element.id);

      if (selectedIds.length) {
        if (selectionModeRef.current === "add") {
          onAddSelectedElements(selectedIds);
        } else {
          onSelectElements(selectedIds);
        }
      } else if (selectionModeRef.current !== "add") {
        onClearSelection();
        onClearMountingHoleSelection();
      }
    } else if (pointerModeRef.current === "click" && pointerStartRef.current && canvas) {
      const deltaX = event.clientX - pointerStartRef.current.x;
      const deltaY = event.clientY - pointerStartRef.current.y;
      const moved = Math.hypot(deltaX, deltaY);

      if (moved < 3) {
        const rect = canvas.getBoundingClientRect();
        const pointPx = {
          x: event.clientX - rect.left,
          y: event.clientY - rect.top,
        };
        const pointPanel = screenPointToPanel(pointPx, transform);
        if (pointPanel) {
          const element = pickElementAtPoint(pointPanel, interactiveElements, {
            isOverMountingHole: findMountingHoleAtPoint(pointPanel, mountingHoles) !== null,
            isPlacing: placementType !== null,
            selectedIds: selectedElementSet,
          });
          if (element) {
            onSelectElement(element.id);
            onClearMountingHoleSelection();
          } else {
            onClearSelection();
            onClearMountingHoleSelection();
            onClearReferenceSelection();
          }
        }
      }
    }

    if (pointerModeRef.current === "clearance") {
      onClearanceLineDragEnd();
    }

    // A right click that stayed put opens the menu of what is under it.
    if (
      pointerModeRef.current === "pan" &&
      panButtonRef.current === 2 &&
      pointerStartRef.current &&
      canvas &&
      Math.hypot(
        event.clientX - pointerStartRef.current.x,
        event.clientY - pointerStartRef.current.y,
      ) < CONTEXT_MENU_MAX_MOVE_PX
    ) {
      const rect = canvas.getBoundingClientRect();
      const pointPanel = screenPointToPanel(
        { x: event.clientX - rect.left, y: event.clientY - rect.top },
        transform,
      );
      const element = pointPanel
        ? pickElementAtPoint(pointPanel, interactiveElements, {
            isOverMountingHole: findMountingHoleAtPoint(pointPanel, mountingHoles) !== null,
            isPlacing: false,
            selectedIds: selectedElementSet,
          })
        : null;
      onRequestContextMenu({
        clientX: event.clientX,
        clientY: event.clientY,
        elementId: element?.id ?? null,
      });
    }

    pointerModeRef.current = "idle";
    pointerStartRef.current = null;
    panStartRef.current = null;
    panButtonRef.current = null;
    patternHandleRef.current = null;
    moveStateRef.current = null;
    referenceInteractionRef.current = null;
    svgArtworkInteractionRef.current = null;
    elementResizeRef.current = null;
    setSelectionRect(null);
    selectionModeRef.current = "replace";
    setPointerPanelPos(null);
    setIsPanning(false);
    setIsHoveringInteractive(false);
    setCanvasCursor(DEFAULT_CANVAS_CURSOR);
    onMoveEnd?.();

    if (canvas) {
      canvas.releasePointerCapture(event.pointerId);

      const rect = canvas.getBoundingClientRect();
      const pointPx = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
      const pointPanel = screenPointToPanel(pointPx, transform);
      updateHoverState(pointPanel, pointPx);
    }
  };

  const handlePointerLeave = (event: React.PointerEvent<HTMLCanvasElement>) => {
    event.stopPropagation();

    pointerModeRef.current = "idle";
    pointerStartRef.current = null;
    panStartRef.current = null;
    panButtonRef.current = null;
    patternHandleRef.current = null;
    setActivePatternHandle(null);
    moveStateRef.current = null;
    referenceInteractionRef.current = null;
    svgArtworkInteractionRef.current = null;
    elementResizeRef.current = null;
    setSelectionRect(null);
    selectionModeRef.current = "replace";
    setIsPanning(false);
    setIsHoveringInteractive(false);
    setCanvasCursor(DEFAULT_CANVAS_CURSOR);
    onMoveEnd?.();
  };

  const handleContextMenu = (event: React.MouseEvent<HTMLCanvasElement>) => {
    event.preventDefault();
  };

  const canvasClassName = [
    styles.canvas,
    isPanning ? styles.canvasPanning : "",
    !isPanning && isHoveringInteractive ? styles.canvasInteractive : "",
  ]
    .filter(Boolean)
    .join(" ");

  return {
    selectionOverlay,
    pointerPanelPos,
    referenceImageElement,
    snapOverridden,
    canvasClassName,
    canvasCursor,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handlePointerLeave,
    handleContextMenu,
    activePatternHandle,
  };
}
